import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Plus, Minus, X } from "lucide-react";
import { formatKobo } from "../lib/format";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import PlaceholderTile from "./PlaceholderTile";

export default function ItemDetailSheet({ item, extras, open, onClose }) {
  const { addItem } = useCart();
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [addons, setAddons] = useState([]);
  const [instructions, setInstructions] = useState("");

  useEffect(() => {
    if (item) {
      setVariant(item.variants?.length ? item.variants[0] : null);
      setQty(1);
      setAddons([]);
      setInstructions("");
    }
  }, [item]);

  if (!item) return null;

  const basePrice = variant ? variant.price : item.price || 0;
  const addonTotal = addons.reduce((s, a) => s + a.price, 0);
  const total = (basePrice + addonTotal) * qty;

  const toggleAddon = (ex) => {
    setAddons((prev) =>
      prev.find((a) => a.id === ex.id) ? prev.filter((a) => a.id !== ex.id) : [...prev, { id: ex.id, name: ex.name, price: ex.price }]
    );
  };

  const handleAdd = () => {
    addItem({
      item_id: item.id,
      name: item.name,
      variant: variant?.name || null,
      unit_price: basePrice,
      image_url: item.image_url,
      quantity: qty,
      addons,
      special_instructions: instructions,
    });
    toast.success(`${item.name} added to cart`);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden rounded-2xl border-sapore-border max-h-[92vh] overflow-y-auto no-scrollbar" data-testid="item-detail-sheet">
        <DialogTitle className="sr-only">{item.name}</DialogTitle>
        <div className="relative aspect-[4/3] bg-sapore-goldSubtle">
          {item.image_url ? (
            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
          ) : (
            <PlaceholderTile />
          )}
          <button onClick={onClose} className="absolute top-3 right-3 bg-white/90 rounded-full p-1.5 text-sapore-ink hover:bg-white" data-testid="item-sheet-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-6">
          <h2 className="font-serif text-2xl font-bold text-sapore-purple">{item.name}</h2>
          {item.description && <p className="text-sapore-inkSoft mt-2 text-sm leading-relaxed">{item.description}</p>}

          {item.allergens?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {item.allergens.map((a) => (
                <span key={a} className="text-[11px] text-sapore-muted bg-sapore-cream border border-sapore-border px-2 py-0.5 rounded-full">
                  {a}
                </span>
              ))}
            </div>
          )}
          {item.prep_note && <p className="text-xs text-sapore-muted mt-2 italic">{item.prep_note}</p>}

          {item.variants?.length > 0 && (
            <div className="mt-5">
              <p className="text-sm font-semibold text-sapore-ink mb-2">Choose size</p>
              <div className="flex gap-2 flex-wrap">
                {item.variants.map((v) => (
                  <button
                    key={v.name}
                    data-testid={`variant-${item.id}-${v.name}`}
                    onClick={() => setVariant(v)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                      variant?.name === v.name
                        ? "bg-sapore-purple text-sapore-cream border-sapore-purple"
                        : "bg-white text-sapore-ink border-sapore-border hover:border-sapore-purple"
                    }`}
                  >
                    {v.name} · {formatKobo(v.price)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {extras?.length > 0 && (
            <div className="mt-5">
              <p className="text-sm font-semibold text-sapore-ink mb-2">Add a side</p>
              <div className="grid grid-cols-2 gap-2">
                {extras.map((ex) => {
                  const active = addons.find((a) => a.id === ex.id);
                  return (
                    <button
                      key={ex.id}
                      data-testid={`addon-${ex.id}`}
                      onClick={() => toggleAddon(ex)}
                      className={`text-left px-3 py-2 rounded-xl text-xs border transition-colors ${
                        active ? "bg-sapore-goldSubtle border-sapore-gold" : "bg-white border-sapore-border hover:border-sapore-gold"
                      }`}
                    >
                      <span className="block font-medium text-sapore-ink">{ex.name}</span>
                      <span className="text-sapore-gold font-semibold">+{formatKobo(ex.price)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-5">
            <p className="text-sm font-semibold text-sapore-ink mb-2">Special instructions</p>
            <textarea
              data-testid="item-instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={2}
              placeholder="e.g. no syrup, extra crispy bacon"
              className="w-full rounded-xl border border-sapore-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-sapore-purple/30"
            />
          </div>

          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center gap-3 border border-sapore-border rounded-full px-2 py-1.5">
              <button data-testid="item-qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-8 h-8 rounded-full hover:bg-sapore-cream flex items-center justify-center">
                <Minus size={16} />
              </button>
              <span className="w-6 text-center font-semibold">{qty}</span>
              <button data-testid="item-qty-increase" onClick={() => setQty((q) => q + 1)} className="w-8 h-8 rounded-full hover:bg-sapore-cream flex items-center justify-center">
                <Plus size={16} />
              </button>
            </div>
            <button
              data-testid="item-add-to-cart"
              onClick={handleAdd}
              className="flex-1 py-3.5 rounded-full bg-sapore-gold text-sapore-ink font-semibold hover:bg-sapore-goldHover transition-colors hover:scale-[1.01] active:scale-[0.99]"
            >
              Add to cart · {formatKobo(total)}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
